import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScenarioPlanner.css';
import CreateProductionPlanModal from '../scenario-planner/CreateProductionPlanModal';
import ProductionPlanSuccessDialog from '../scenario-planner/ProductionPlanSuccessDialog';
import { FORMAT_DEFINITIONS } from '../scenario-planner/constants';
import {
    getQuantityValueForFormat,
    getTargetBottleneck,
} from '../scenario-planner/scenarioCalculations';

import { useScenarioPlannerWithRevenue } from '../scenario-planner/useScenarioPlannerWithRevenue';
import {
    ScenarioPlannerEmptyState,
    ScenarioPlannerLoadingState,
} from '../scenario-planner/ScenarioPlannerStates';
import GuidedStickyHeader from '../scenario-planner/GuidedStickyHeader';
import StepProgressBar from '../scenario-planner/StepProgressBar';
import WizardStep1Family from '../scenario-planner/WizardStep1Family';
import WizardStep2Format from '../scenario-planner/WizardStep2Format';
import WizardStep3Quantity from '../scenario-planner/WizardStep3Quantity';
import WizardStep4Review from '../scenario-planner/WizardStep4Review';
import ScenarioSummaryPanel from '../scenario-planner/ScenarioSummaryPanel';

function ScenarioPlannerWithRevenue() {
    const navigate = useNavigate();
    const planner = useScenarioPlannerWithRevenue();
    const { actions, planModal } = planner;

    const [currentStep, setCurrentStep] = useState(1);
    const [maxStepRevealed, setMaxStepRevealed] = useState(1);
    const [highlightedStep, setHighlightedStep] = useState(null);

    const step1Ref = useRef(null);
    const step2Ref = useRef(null);
    const step3Ref = useRef(null);
    const step4Ref = useRef(null);

    // ─── Active product ──────────────────────────────────────────────────────
    const activeProduct =
        planner.displayedProducts.find(p => p.product_id === planner.activeProductId) ||
        planner.displayedProducts[0];

    // ─── Derived selections ───────────────────────────────────────────────────
    const selectedFamily = activeProduct
        ? planner.activeFormatFamilyByProduct[activeProduct.product_id]
        : null;

    const selectedFormatId = activeProduct
        ? planner.focusedFormatByProduct[activeProduct.product_id]
        : null;

    const selectedFormat = FORMAT_DEFINITIONS[selectedFormatId];

    const availableKegs = activeProduct
        ? actions.getAvailableKegsForProduct(activeProduct)
        : 0;

    // The quantity that's actually set for the selected format — matches
    // exactly what Step 3 displays, including its pre-filled default in
    // capacity mode, so "can I create a plan" never disagrees with what's
    // shown on screen.
    const quantityValue = (!activeProduct || !selectedFormatId)
        ? 0
        : getQuantityValueForFormat(
              planner.viewMode,
              planner.formatAllocations,
              planner.targetAllocations,
              activeProduct.product_id,
              selectedFormatId,
              availableKegs
          );

    // ─── Step completion flags ────────────────────────────────────────────────
    const step1Complete = Boolean(selectedFamily);
    const step2Complete = Boolean(selectedFormatId);
    const step3Complete = quantityValue > 0;

    const kegsNeeded =
        planner.viewMode === 'target'
            ? selectedFormat
                ? quantityValue / selectedFormat.per_concentrate_keg
                : 0
            : quantityValue;

    const canCreatePlan = step3Complete && kegsNeeded <= availableKegs;

    const completedSteps = [
        step1Complete ? 1 : null,
        step2Complete ? 2 : null,
        step3Complete ? 3 : null,
    ].filter(Boolean);

    // ─── Reset wizard when the active product changes ─────────────────────────
    useEffect(() => {
        setCurrentStep(1);
        setMaxStepRevealed(1);
    }, [planner.activeProductId]);

    // ─── Scroll + highlight helpers ──────────────────────────────────────────
    const STEP_REFS = { 1: step1Ref, 2: step2Ref, 3: step3Ref, 4: step4Ref };

    const scrollTo = (ref) => {
        if (ref?.current) {
            setTimeout(() => {
                ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        }
    };

    const scrollAndHighlight = (stepNum, ref) => {
        scrollTo(ref);
        setHighlightedStep(stepNum);
        setTimeout(() => setHighlightedStep(null), 1400);
    };

    // ─── Wizard event handlers ────────────────────────────────────────────────
    const handleFamilySelect = (familyId) => {
        if (!activeProduct) return;

        // If a different family is selected, also clear the focused format selection
        const prevFamily = planner.activeFormatFamilyByProduct[activeProduct.product_id];
        actions.setActiveFormatFamily(activeProduct.product_id, familyId);

        if (prevFamily !== familyId && selectedFormatId) {
            // Disable the previously enabled format so Step 2 resets cleanly
            const wasEnabled = Boolean(
                planner.enabledFormats[activeProduct.product_id]?.[selectedFormatId]
            );
            if (wasEnabled) {
                actions.toggleFormatEnabled(activeProduct.product_id, selectedFormatId);
            }
        }

        setMaxStepRevealed(prev => Math.max(prev, 2));
        setCurrentStep(2);
        scrollTo(step2Ref);
    };

    const handleFormatSelect = (formatId) => {
        if (!activeProduct) return;

        const alreadyEnabled = Boolean(
            planner.enabledFormats[activeProduct.product_id]?.[formatId]
        );

        actions.setFocusedFormat(activeProduct.product_id, formatId);
        if (!alreadyEnabled) {
            actions.toggleFormatEnabled(activeProduct.product_id, formatId);
        }

        setMaxStepRevealed(prev => Math.max(prev, 3));
        setCurrentStep(3);
        scrollTo(step3Ref);
    };

    const handleReviewCreate = () => {
        setMaxStepRevealed(prev => Math.max(prev, 4));
        setCurrentStep(4);
        scrollTo(step4Ref);
    };

    const handleStepClick = (step) => {
        const isAccessible = step === 1 || completedSteps.includes(step - 1) || step <= maxStepRevealed;
        if (!isAccessible) return;

        // Ensure the target step is marked as unlocked
        setMaxStepRevealed(prev => Math.max(prev, step));
        setCurrentStep(step);

        // Scroll to and briefly highlight the target section
        const ref = STEP_REFS[step];
        scrollAndHighlight(step, ref);
    };

    // ─── Scroll Spy / Active Step Tracking ─────────────────────────────────────
    useEffect(() => {
        if (!activeProduct) return;

        const observerOptions = {
            root: null,
            rootMargin: '-140px 0px -60% 0px',
            threshold: 0,
        };

        const observerCallback = (entries) => {
            const visibleEntries = entries.filter(e => e.isIntersecting);
            if (visibleEntries.length > 0) {
                visibleEntries.sort((a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top);
                const activeStepNum = Number(visibleEntries[0].target.getAttribute('data-step'));
                if (activeStepNum) {
                    setCurrentStep(activeStepNum);
                }
            }
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        const steps = [
            { id: 1, ref: step1Ref },
            { id: 2, ref: step2Ref },
            { id: 3, ref: step3Ref },
            { id: 4, ref: step4Ref },
        ];

        steps.forEach(({ id, ref }) => {
            if (ref.current) {
                ref.current.setAttribute('data-step', id.toString());
                observer.observe(ref.current);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, [activeProduct, maxStepRevealed]);

    // ─── Target bottleneck (target mode only) ─────────────────────────────────
    const targetBottleneck =
        activeProduct && planner.viewMode === 'target'
            ? getTargetBottleneck(
                  activeProduct,
                  planner.targetAllocations,
                  planner.finishedKegs
              )
            : null;

    // ─── Why the Create Production Plan button is disabled, in plain terms ────
    // Shared across the sticky header, the summary panel, and Step 4's review
    // button so they never disagree about the reason, and never fall back to a
    // generic "complete all steps" message when the steps are in fact complete.
    let blockedReason = null;
    if (!canCreatePlan) {
        if (!step1Complete) {
            blockedReason = 'Choose a product family to continue';
        } else if (!step2Complete) {
            blockedReason = 'Choose a format to continue';
        } else if (!step3Complete) {
            if (planner.viewMode === 'capacity' && availableKegs <= 0) {
                blockedReason = 'No concentrate is currently available for this product';
            } else if (planner.viewMode === 'capacity') {
                blockedReason = 'All available concentrate is already allocated to other formats for this product';
            } else {
                blockedReason = 'Enter a target quantity to continue';
            }
        } else if (kegsNeeded > availableKegs) {
            const shortfallKegs = kegsNeeded - availableKegs;
            const limitingName = targetBottleneck?.limitingIngredients?.[0]?.ingredient_name;
            blockedReason = limitingName
                ? `Limited by ${limitingName} — short ${shortfallKegs.toFixed(2)} concentrate kegs`
                : `Needs ${shortfallKegs.toFixed(2)} more concentrate kegs than available`;
        }
    }

    // ─── Empty / loading guards ───────────────────────────────────────────────
    if (planner.selectedProductIds.length === 0) {
        return <ScenarioPlannerEmptyState />;
    }

    if (!planner.scenarioCapacity) {
        return <ScenarioPlannerLoadingState />;
    }

    const showWizard = Boolean(activeProduct);

    return (
        <div className="gsp-root">

            {/* ── Sticky dark header ── */}
            <GuidedStickyHeader
                activeProduct={activeProduct}
                availableKegs={availableKegs}
                selectedFormatId={selectedFormatId}
                quantityValue={quantityValue}
                viewMode={planner.viewMode}
                canCreatePlan={canCreatePlan}
                blockedReason={blockedReason}
                onCreatePlan={actions.openPlanModal}
            />

            {/* ── Sticky Progress Bar ── */}
            {showWizard && (
                <div className="gsp-sticky-progress-wrapper">
                    <StepProgressBar
                        currentStep={currentStep}
                        completedSteps={completedSteps}
                        onStepClick={handleStepClick}
                        maxStepRevealed={maxStepRevealed}
                    />
                </div>
            )}

            {/* ── Actions Row (Non-sticky) ── */}
            {showWizard && (
                <div className="gsp-progress-actions-row">
                    {/* Small mode toggle — preserved from original functionality */}
                    <div className="gsp-mode-toggle">
                        <button
                            className={`gsp-mode-btn ${planner.viewMode === 'capacity' ? 'active' : ''}`}
                            onClick={() => actions.setViewMode('capacity')}
                            title="Allocate available concentrate kegs"
                        >
                            By Capacity
                        </button>
                        <button
                            className={`gsp-mode-btn ${planner.viewMode === 'target' ? 'active' : ''}`}
                            onClick={() => actions.setViewMode('target')}
                            title="Set target output quantities"
                        >
                            By Target
                        </button>
                    </div>

                    <button
                        className="gsp-reset-btn"
                        onClick={actions.resetScenario}
                        title="Reset all scenario inputs"
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                            <path d="M3 2v6h6" /><path d="M3 13a9 9 0 1 0 3-7.7L3 8" />
                        </svg>
                        Reset
                    </button>
                </div>
            )}

            {/* ── Main layout: wizard (col 1) + summary panel (col 2) ── */}
            <div className="gsp-layout">

                {/* ── Left column: wizard steps ── */}
                <div className="gsp-wizard-area">

                    {/* Product switcher — inside wizard col so grid isn't broken */}
                    {planner.displayedProducts.length > 1 && (
                        <div className="gsp-product-switcher">
                            <span className="gsp-product-switcher-label">Products</span>
                            <div className="gsp-product-switcher-pills">
                                {planner.displayedProducts.map((product, index) => (
                                    <button
                                        key={product.product_id}
                                        className={`gsp-product-pill ${
                                            planner.activeProductId === product.product_id ? 'active' : ''
                                        }`}
                                        onClick={() => actions.setActiveProduct(product.product_id)}
                                    >
                                        <span className="gsp-product-pill-index">{index + 1}</span>
                                        {product.product_name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showWizard && (
                        <>
                            {/* Step 1 – always visible once wizard is shown */}
                            <div
                                ref={step1Ref}
                                className={`gsp-step-scroll-target ${highlightedStep === 1 ? 'gsp-step-highlight' : ''}`}
                            >
                                <WizardStep1Family
                                    selectedFamily={selectedFamily}
                                    onSelectFamily={handleFamilySelect}
                                />
                            </div>

                            {/* Step 2 – revealed after Step 1 */}
                            {maxStepRevealed >= 2 && (
                                <div
                                    ref={step2Ref}
                                    className={`gsp-step-scroll-target ${highlightedStep === 2 ? 'gsp-step-highlight' : ''}`}
                                >
                                    <WizardStep2Format
                                        selectedFamily={selectedFamily}
                                        selectedFormatId={selectedFormatId}
                                        availableKegs={availableKegs}
                                        formatAllocations={planner.formatAllocations}
                                        activeProductId={activeProduct.product_id}
                                        viewMode={planner.viewMode}
                                        onSelectFormat={handleFormatSelect}
                                    />
                                </div>
                            )}

                            {/* Step 3 – revealed after Step 2 */}
                            {maxStepRevealed >= 3 && (
                                <div
                                    ref={step3Ref}
                                    className={`gsp-step-scroll-target ${highlightedStep === 3 ? 'gsp-step-highlight' : ''}`}
                                >
                                    <WizardStep3Quantity
                                        activeProduct={activeProduct}
                                        availableKegs={availableKegs}
                                        selectedFormatId={selectedFormatId}
                                        formatAllocations={planner.formatAllocations}
                                        targetAllocations={planner.targetAllocations}
                                        finishedKegs={planner.finishedKegs}
                                        scenarioInventory={planner.scenarioInventory}
                                        showStandardized={planner.showStandardized}
                                        targetBottleneck={targetBottleneck}
                                        viewMode={planner.viewMode}
                                        onFormatAllocation={actions.handleFormatAllocation}
                                        onTargetAllocation={actions.handleTargetAllocation}
                                        onFinishedKegsChange={actions.handleFinishedKegsChange}
                                        onInventoryChange={actions.handleInventoryChange}
                                        onReviewCreate={handleReviewCreate}
                                    />
                                </div>
                            )}

                            {/* Step 4 – revealed when user clicks Review & Create */}
                            {maxStepRevealed >= 4 && (
                                <div
                                    ref={step4Ref}
                                    className={`gsp-step-scroll-target ${highlightedStep === 4 ? 'gsp-step-highlight' : ''}`}
                                >
                                    <WizardStep4Review
                                        activeProduct={activeProduct}
                                        selectedFamily={selectedFamily}
                                        selectedFormatId={selectedFormatId}
                                        quantityValue={quantityValue}
                                        viewMode={planner.viewMode}
                                        availableKegs={availableKegs}
                                        canCreatePlan={canCreatePlan}
                                        blockedReason={blockedReason}
                                        onBack={() => setCurrentStep(3)}
                                        onCreatePlan={actions.openPlanModal}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Right sticky summary panel */}
                {showWizard && (
                    <ScenarioSummaryPanel
                        activeProduct={activeProduct}
                        selectedFamily={selectedFamily}
                        selectedFormatId={selectedFormatId}
                        quantityValue={quantityValue}
                        viewMode={planner.viewMode}
                        canCreatePlan={canCreatePlan}
                        blockedReason={blockedReason}
                        onCreatePlan={actions.openPlanModal}
                    />
                )}
            </div>

            {/* Production plan modal — unchanged */}
            {planModal.showPlanModal && (
                <CreateProductionPlanModal
                    displayedProducts={planner.displayedProducts}
                    formatAllocations={planner.formatAllocations}
                    modal={planModal}
                    targetAllocations={planner.targetAllocations}
                    viewMode={planner.viewMode}
                    getAllocatedKegsForProduct={actions.getAllocatedKegsForProduct}
                    getAvailableKegsForProduct={actions.getAvailableKegsForProduct}
                    onClose={actions.closePlanModal}
                    onCreatePlan={actions.createProductionPlan}
                    onPlanEndDateChange={actions.setPlanEndDate}
                    onPlanNameChange={actions.setPlanName}
                    onPlanNotesChange={actions.setPlanNotes}
                    onPlanStartDateChange={actions.setPlanStartDate}
                />
            )}

            {planModal.createdPlan && (
                <ProductionPlanSuccessDialog
                    plan={planModal.createdPlan}
                    onClose={() => actions.setCreatedPlan(null)}
                    onViewPlan={() => {
                        actions.setCreatedPlan(null);
                        navigate('/production-plans');
                    }}
                />
            )}
        </div>
    );
}

export default ScenarioPlannerWithRevenue;
